/**
 * Determines whether a given email is restricted based on a list of restricted domains.
 * This function is typescript implementation of php function: Kms_Helper_EmailLists::isEmailInString (https://github.com/kaltura/mediaspace/blob/575b8758de01668d00c67f99515880b8f7605d2e/library/Kms/Helper/EmailLists.php#L16)
 *
 * Restriction is checked in two ways:
 *   1. Direct match against the restricted domain list
 *   2. Match against "wildcard" variations of the email
 *
 * Example:
 *   email: "user.name@sub.domain.com"
 *   restrictedDomains: ["sub.domain.com", "user.name@*", "*@domain.com", "user.name@*.domain.com"]
 *
 *   → isEmailRestricted("user.name@sub.domain.com", ["sub.domain.com", "user.name@*", "*@domain.com", "user.name@*.domain.com"]) === true
 */
export const isEmailRestricted = (email: string, restrictedDomains: string[]) => {
    // Normalize the email to lowercase so that comparison is case-insensitive.
    const lowerEmail = email.toLowerCase();

    /**
     * Build all possible "wildcard" variations of the email.
     *
     * Wildcards allow restrictions that are more flexible than direct matches.
     * For example, if the email is `user@a.b.c`:
     *   - `user@*`             → restrict this user at any domain
     *   - `*@a.b.c`            → restrict all users at this exact domain
     *   - `a.b.c`              → restrict by bare domain (no user part)
     *   - `user@*.b.c`         → restrict this user at any subdomain of b.c
     *   - `*@a.*.c`            → restrict any user at domains with a wildcard in the middle
     *   - `a.b.*`              → restrict by wildcard in the last domain part
     *
     * This gives flexibility for admins to block entire domains or domain patterns.
     */
    const buildWildcards = (email: string) => {
        // Split email into local-part (user) and domain-part (everything after "@")
        const [userPart, domainPart] = email.split("@");
        // Split domain into its segments (e.g. "sub.domain.com" → ["sub","domain","com"])
        const domainParts = domainPart.split(".");

        // Start with the most common wildcard forms:
        // - user at any domain
        // - any user at this exact domain
        // - just the domain itself
        const wildcards: string[] = [`${userPart}@*`, `*@${domainPart}`, domainPart];

        // For each domain segment, replace it with "*" and generate patterns
        // Example: "sub.domain.com"
        //   Iteration 0 → ["*", "domain", "com"] → user@*.domain.com, *@*.domain.com, *.domain.com
        //   Iteration 1 → ["sub", "*", "com"]    → user@sub.*.com, *@sub.*.com, sub.*.com
        //   Iteration 2 → ["sub", "domain", "*"] → user@sub.domain.*, *@sub.domain.*, sub.domain.*
        domainParts.forEach((_, i) => {
            const copyDomainParts = [...domainParts];
            copyDomainParts[i] = "*";
            wildcards.push(`${userPart}@${copyDomainParts.join(".")}`);
            wildcards.push(`*@${copyDomainParts.join(".")}`);
            wildcards.push(copyDomainParts.join("."));
        });

        return wildcards;
    };

    // Build all wildcard patterns for this email.
    // Example: "user@sub.domain.com" → [
    //   "user@*", "*@sub.domain.com", "sub.domain.com",
    //   "user@*.domain.com", "*@*.domain.com", "*.domain.com",
    //   "user@sub.*.com", "*@sub.*.com", "sub.*.com",
    //   "user@sub.domain.*", "*@sub.domain.*", "sub.domain.*"
    // ]
    const potentialWildcards = buildWildcards(lowerEmail);

    // Finally, check if the email is restricted:
    // - Either a direct case-insensitive match with the restricted domain
    // - Or a match with one of the wildcard variations
    return restrictedDomains.some((domain) => {
        const lowerDomain = domain.toLowerCase();
        return lowerEmail === lowerDomain || potentialWildcards.includes(lowerDomain);
    });
};
